// src/pages/ops/MechanicTodo.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy } from "react-table";
import axios from "axios";
import { format } from "date-fns";
import ExternalBookingModal from "../../components/externalBookingModal.jsx";

/* ────────────────────────────────────────────────────────────── */
/* helper: generic full‑height table card                         */
/* ────────────────────────────────────────────────────────────── */
const SimpleTable = ({
  title,
  data,
  columns,
  onRowClick,
  badgeColour = "bg-joule",
}) => {
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { sortBy: [{ id: "receivedAt", desc: true }] },
    },
    useSortBy
  );

  return (
    /* full‑height column */
    <div className="w-full lg:w-1/2 p-4">
      {/* card fills almost the whole viewport height */}
      <div className="h-[calc(100vh-2rem)] flex flex-col rounded-xl border border-gray-300 shadow-2xl bg-white">
        {/* header */}
        <header className="px-5 py-4 border-b border-gray-200 flex justify-center items-center">
          <h2 className="text-lg font-semibold flex items-center">
            {title}
            <span
              className={`${badgeColour} text-white text-xs px-3 py-1 rounded-full ml-3`}
            >
              {tableInstance.rows.length}
            </span>
          </h2>
        </header>

        {/* scrollable body */}
        <div className="flex-1 overflow-auto">
          <table
            {...tableInstance.getTableProps()}
            className="min-w-full divide-y divide-gray-200 text-center"
          >
            {/* ────── HEADERS ────── */}
            <thead className="bg-gray-800 text-white sticky top-0 z-10">
              {tableInstance.headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      className="px-4 py-4 text-xs font-semibold uppercase tracking-wider"
                    >
                      {col.render("Header")}
                      <span className="ml-1">
                        {col.isSorted ? (col.isSortedDesc ? "🔽" : "🔼") : ""}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            {/* ────── ROWS ────── */}
            <tbody
              {...tableInstance.getTableBodyProps()}
              className="divide-y divide-gray-100"
            >
              {tableInstance.rows.map((row) => {
                tableInstance.prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onRowClick(row.original)}
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="px-4 py-3 text-sm whitespace-nowrap"
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
/* main page component                                           */
/* ────────────────────────────────────────────────────────────── */
const ExternalServiceToDoList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  /* fetch once */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/external-bookings");
        setBookings(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* optimistic update from modal */
  const handleUpdated = useCallback(
    (updated) =>
      setBookings((prev) =>
        prev.map((b) => (b._id === updated._id ? updated : b))
      ),
    []
  );

  /* split datasets */
  const waitingForInspection = useMemo(
    () => bookings.filter((b) => b.status === "Received"),
    [bookings]
  );
  const readyForService = useMemo(
    () => bookings.filter((b) => b.status === "ReadyForService"),
    [bookings]
  );

  /* shared columns */
  const baseCols = [
    {
      Header: "Received",
      accessor: "receivedAt",
      Cell: ({ value }) => (value ? format(new Date(value), "dd.MM") : "—"),
    },
    {
      Header: "Booking ID",
      accessor: "externalBookingId",
    },
    {
      Header: "Bike",
      accessor: (r) => `${r.bikeBrand} ${r.bikeModel}`,
      id: "bike",
    },
    {
      Header: "Package",
      accessor: "packageName",
    },
    {
      Header: "Customer",
      accessor: (r) => `${r.firstName} ${r.lastName}`,
      id: "cust",
    },
  ];

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="flex flex-wrap">
      {/* waiting for inspection */}
      <SimpleTable
        title="🛠️ Waiting for inspection"
        data={waitingForInspection}
        columns={baseCols}
        onRowClick={setSelected}
        badgeColour="bg-yellow-600"
      />

      {/* ready for service */}
      <SimpleTable
        title="🔧 Ready for service"
        data={readyForService}
        columns={baseCols}
        onRowClick={setSelected}
        badgeColour="bg-green-600"
      />

      {/* modal */}
      {selected && (
        <ExternalBookingModal
          isOpen
          booking={selected}
          closeModal={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
};

export default ExternalServiceToDoList;
